import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, ArrowLeft, Download, Check, Palette, Save, Printer } from "lucide-react";
import { PackageData } from "@/pages/CreatePackage";
import { useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "@/hooks/useSupabaseAuth";
import { ProgressSteps } from "./ProgressSteps";
import { usePackageSave } from "@/hooks/usePackageSave";
import { Progress } from "@/components/ui/progress";
interface SummaryStepProps {
  data: PackageData;
  onPrev: () => void;
  onFinish: (templateId: string) => void;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  isStepRequired?: (step: number) => boolean;
}
export const SummaryStep = ({
  data,
  onPrev,
  onFinish,
  completedSteps = [],
  onStepClick = () => {},
  isStepRequired = () => true
}: SummaryStepProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState('template1');
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { savePackage, isSaving, saveProgress } = usePackageSave();
  const templates = [{
    id: 'template1',
    name: 'تصميم عصري',
    description: 'ألوان جذابة وتصميم حيوي',
    style: 'bg-gradient-to-br from-purple-200 to-blue-200',
    textColor: 'text-purple-800'
  }, {
    id: 'template2',
    name: 'تصميم احترافي',
    description: 'نظيف ورسمي للشركات',
    style: 'bg-gray-100 border border-gray-300',
    textColor: 'text-gray-800'
  }, {
    id: 'template3',
    name: 'تصميم أنيق',
    description: 'داكن وفاخر للتجارب المميزة',
    style: 'bg-gray-800',
    textColor: 'text-yellow-400'
  }, {
    id: 'template4',
    name: 'إبداع رقمي',
    description: 'تقني وداكن مع تدرجات لونية',
    style: 'bg-gradient-to-br from-cyan-900 to-blue-900',
    textColor: 'text-cyan-300'
  }, {
    id: 'template5',
    name: 'فخامة كلاسيكية',
    description: 'أنيق وبسيط بخطوط serif',
    style: 'bg-yellow-50 border border-yellow-200',
    textColor: 'text-rose-900'
  }, {
    id: 'template6',
    name: 'تقنية متقدمة',
    description: 'بسيط وعملي مع لمسة عصرية',
    style: 'bg-violet-100 border border-violet-300',
    textColor: 'text-violet-800'
  }];
  const handleSaveAsDraft = async () => {
    const result = await savePackage(data, 'draft');
    if (result.success) navigate('/tourism-packages');
  };

  const handleSaveAndPublish = async () => {
    const result = await savePackage(data, 'active');
    if (result.success) navigate('/tourism-packages');
  };

  const handleSaveAndPrint = async () => {
    const result = await savePackage(data, 'active');
    if (result.success && selectedTemplate) {
      await onFinish(selectedTemplate);
      navigate('/tourism-packages');
    }
  };

  const handlePrintOnly = async () => {
    if (selectedTemplate) {
      await onFinish(selectedTemplate);
    }
  };
  return <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          ملخص العرض
        </CardTitle>
        
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Auth Status Warning */}
        {!user && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-yellow-900">تنبيه</h3>
                <p className="text-yellow-700 text-sm">يجب تسجيل الدخول لحفظ العرض في قاعدة البيانات</p>
              </div>
            </div>
          </div>}

        {/* Save Progress Indicator */}
        {isSaving && saveProgress > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-blue-900">جاري الحفظ...</h3>
                <span className="text-sm text-blue-700">{saveProgress}%</span>
              </div>
              <Progress value={saveProgress} className="h-2" />
              <p className="text-xs text-blue-600">
                {saveProgress < 20 && "جاري معالجة بيانات العميل..."}
                {saveProgress >= 20 && saveProgress < 40 && "جاري حفظ الباقة الأساسية..."}
                {saveProgress >= 40 && saveProgress < 60 && "جاري حفظ تفاصيل الفنادق والباقة..."}
                {saveProgress >= 60 && saveProgress < 75 && "جاري حفظ الشمول والاستثناءات..."}
                {saveProgress >= 75 && saveProgress < 90 && "جاري حفظ الرحلات والنقل والجولات..."}
                {saveProgress >= 90 && saveProgress < 92 && "إنشاء مهمة المتابعة..."}
                {saveProgress >= 92 && saveProgress < 96 && "إرسال الإشعارات..."}
                {saveProgress >= 96 && saveProgress < 100 && "إنشاء مهام العمل..."}
                {saveProgress === 100 && "اكتمل الحفظ بنجاح!"}
              </p>
            </div>
          </div>
        )}

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-900">العرض جاهز للمراجعة!</h3>
              <p className="text-green-700">تم إكمال جميع الخطوات بنجاح.</p>
            </div>
          </div>
        </div>

        {/* Package Title Section */}
        {data.packageName && <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-blue-900 mb-2">
                {data.packageName}
              </h2>
              <p className="text-blue-700">اسم العرض السياحي</p>
            </div>
          </div>}

        {/* Summary Content */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">معلومات العميل</h4>
              <p className="text-sm text-gray-600">
                <strong>الاسم:</strong> {data.clientName}<br />
                <strong>النوع:</strong> {data.clientType === 'b2b' ? 'شراكة تجارية' : 'عميل فردي'}<br />
                <strong>البريد:</strong> {data.clientEmail}
              </p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">تفاصيل العرض</h4>
              <p className="text-sm text-gray-600">
                <strong>اسم العرض:</strong> {data.packageName || 'عرض سياحي مخصص'}<br />
                <strong>الوجهة:</strong> {data.destination || 'غير محدد'}<br />
                <strong>المدة:</strong> {data.duration || 'غير محدد'}
              </p>
            </div>
          </div>

          <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                ${data.totalPrice || 0}
              </div>
              <p className="text-blue-700">إجمالي سعر العرض</p>
            </div>
          </div>
        </div>

        {/* Template Selection */}
        <div className="pt-6 border-t">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5 text-gray-600" />
              اختر قالب الـ PDF
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {templates.map(template => <div key={template.id} className={`cursor-pointer border-2 p-4 rounded-lg text-center transition-all duration-300 transform hover:scale-105 ${selectedTemplate === template.id ? 'border-blue-500 bg-blue-50 shadow-lg' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedTemplate(template.id)}>
                  <div className={`h-20 rounded-md mb-2 flex items-center justify-center p-2 ${template.style}`}>
                      <p className={`text-sm font-bold ${template.textColor}`}>{template.name}</p>
                  </div>
                  <p className="text-xs text-gray-600">{template.description}</p>
                </div>)}
            </div>
        </div>

        <div className="flex flex-col gap-4 pt-6 border-t">
          {/* Action Buttons Row 1 */}
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={onPrev} disabled={isSaving}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              السابق
            </Button>
            
            <div className="flex gap-3">
              <Button onClick={handlePrintOnly} disabled={!selectedTemplate} variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50">
                <Printer className="w-4 h-4 ml-2" />
                طباعة فقط
              </Button>
              
              <Button onClick={handleSaveAsDraft} disabled={isSaving} variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                <Save className="w-4 h-4 ml-2" />
                {isSaving ? "جاري الحفظ..." : "حفظ كمسودة"}
              </Button>
            </div>
          </div>

          {/* Action Buttons Row 2 */}
          <div className="flex justify-end gap-3">
            <Button onClick={handleSaveAndPublish} disabled={isSaving} className="bg-green-600 hover:bg-green-700 text-white">
              <Save className="w-4 h-4 ml-2" />
              {isSaving ? "جاري الحفظ..." : "حفظ ونشر"}
            </Button>
            
            <Button onClick={handleSaveAndPrint} disabled={!selectedTemplate || isSaving} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg">
              <Download className="w-4 h-4 ml-2" />
              {isSaving ? "جاري الحفظ..." : "حفظ وطباعة"}
            </Button>
          </div>
        </div>
      </CardContent>
      
      {/* Progress Steps */}
      <ProgressSteps 
        currentStep={9} 
        completedSteps={completedSteps} 
        onStepClick={onStepClick} 
        isStepRequired={isStepRequired} 
      />
    </Card>;
};